'use client';

import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import { css } from '@/styled-system/css';
import { Banner, Menu, Text, TextButton } from '@zillow/constellation-10';
import { ArrowUpDown, Check } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useFilters } from '@/src/lib/filters';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { emitTrackSortListingsEvent } from '@/src/analytics/hotpads/events';
import { SrpSaveSearchButton } from './SrpSaveSearchButton';

type Props = {
  pageTitle: string;
  isViewingDetectedArea: boolean;
  /**
   * The server-computed `effectiveFilters.orderBy`. The filter provider is seeded from an effect,
   * which never runs on the server, so without this the first paint always reads "Recommended"
   * and then visibly switches on hydration.
   */
  initialOrderBy?: FilterState['orderBy'];
  /** Streamed count line (client component that tracks live listings query). */
  countsSlot: ReactNode;
};

const SORT_OPTIONS: Array<{
  value: string;
  labelKey: 'sortRecommended' | 'sortNewest' | 'sortMostPopular' | 'sortHighPrice' | 'sortLowPrice';
  legacyTestId: string;
  semanticTestId: string;
}> = [
  {
    // 'score' rather than a null/absent param: on a slug that defaults to newest, an absent orderBy
    // means "use the slug default", so recommended needs a value of its own to be expressible.
    value: 'score',
    labelKey: 'sortRecommended',
    legacyTestId: 'sort-option-recommended',
    semanticTestId: 'sort-option-relevance',
  },
  {
    value: 'activated',
    labelKey: 'sortNewest',
    legacyTestId: 'sort-option-activated',
    semanticTestId: 'sort-option-newest',
  },
  {
    value: 'weekViews',
    labelKey: 'sortMostPopular',
    legacyTestId: 'sort-option-weekViews',
    semanticTestId: 'sort-option-most-popular',
  },
  {
    value: 'highPrice',
    labelKey: 'sortHighPrice',
    legacyTestId: 'sort-option-highPrice',
    semanticTestId: 'sort-option-price-high',
  },
  {
    value: 'lowPrice',
    labelKey: 'sortLowPrice',
    legacyTestId: 'sort-option-lowPrice',
    semanticTestId: 'sort-option-price-low',
  },
];

const actionsNavRoot = css({
  display: 'flex',
  flexDirection: 'column',
  background: 'bg.neutral',
  paddingBottom: 'tight',
  width: '100%',
  position: 'relative',
});

const summaryAndActionsRow = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gridTemplateAreas: '"title title" "count count" "actions actions"',
  columnGap: 'default',
  rowGap: 'tightest',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  lg: {
    gridTemplateAreas: '"title title" "count actions"',
  },
});

const title = css({
  gridArea: 'title',
  margin: 0,
  minWidth: 0,
});

const countContainer = css({
  gridArea: 'count',
  minWidth: 0,
});

const metadataActionControls = css({
  gridArea: 'actions',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'tighter',
  width: '100%',
  minWidth: 0,
  lg: {
    justifyContent: 'flex-end',
    justifySelf: 'end',
    width: 'auto',
  },
});

const actionControlItem = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

export function ActionsNavBarClient({ pageTitle, isViewingDetectedArea, initialOrderBy, countsSlot }: Props) {
  const t = useTranslations('search');
  const sortOptions = useMemo(() => SORT_OPTIONS.map((opt) => ({ ...opt, label: t(opt.labelKey) })), [t]);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const actionsNavRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();
  const { filters, setFilters } = useFilters();

  const sortOrder = filters.orderBy ?? initialOrderBy ?? 'score';
  const sortLabel = sortOptions.find((o) => o.value === sortOrder)?.label ?? t('sortRecommended');

  useLayoutEffect(() => {
    if (!isDesktop) return;

    const actionsNav = actionsNavRef.current;
    const searchHeader = document.querySelector<HTMLElement>('[data-srp-component="search-header"]');
    const scrollContainer = (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
    if (!actionsNav || !searchHeader) return;

    const previousScrollPaddingTop = scrollContainer.style.scrollPaddingTop;
    const syncScrollPaddingTop = () => {
      scrollContainer.style.scrollPaddingTop = `${searchHeader.offsetHeight + actionsNav.offsetHeight}px`;
    };

    syncScrollPaddingTop();
    window.addEventListener('resize', syncScrollPaddingTop);

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncScrollPaddingTop);
    resizeObserver?.observe(searchHeader);
    resizeObserver?.observe(actionsNav);

    return () => {
      window.removeEventListener('resize', syncScrollPaddingTop);
      resizeObserver?.disconnect();
      scrollContainer.style.scrollPaddingTop = previousScrollPaddingTop;
    };
  }, [isDesktop]);

  const handleSortChange = (value: string) => {
    if (value !== sortOrder) {
      emitTrackSortListingsEvent({ triggerLocation: 'search_results', triggerObject: 'search_results|header' });
    }
    setFilters({ ...filters, orderBy: value });
    setIsSortOpen(false);
  };

  const sortControl = (
    <Menu
      placement="bottom-start"
      open={isSortOpen}
      onOpenChange={setIsSortOpen}
      trigger={
        <TextButton.Root
          type="button"
          tone="neutral"
          textStyle="body-sm-bold"
          aria-label={`${t('sortPrefix')} ${sortLabel}`}
          data-testid="button-sort"
        >
          <TextButton.Icon size="sm">
            <LucideIcon icon={ArrowUpDown} size="sm" />
          </TextButton.Icon>
          <TextButton.Label>{sortLabel}</TextButton.Label>
        </TextButton.Root>
      }
      content={
        <div data-testid="sort-dropdown">
          {sortOptions.map((opt) => {
            const selected = opt.value === sortOrder;
            return (
              <Menu.Item key={opt.value} onClick={() => handleSortChange(opt.value)} data-testid={opt.legacyTestId}>
                {selected && (
                  <Menu.ItemIcon>
                    <LucideIcon icon={Check} size="sm" />
                  </Menu.ItemIcon>
                )}
                <Menu.ItemLabel>
                  <span data-testid={opt.semanticTestId}>{opt.label}</span>
                </Menu.ItemLabel>
              </Menu.Item>
            );
          })}
        </div>
      }
    />
  );

  return (
    <div
      ref={actionsNavRef}
      id="srp-actions-nav"
      data-srp-component="actions-nav"
      data-testid="srp-navbar"
      className={actionsNavRoot}
    >
      <div className={summaryAndActionsRow}>
        <Text asChild textStyle="body-lg-bold">
          <h1 data-srp-component="summary-title" className={title}>
            {pageTitle}
          </h1>
        </Text>
        <div id="srp-listings-count" data-srp-component="listings-count" className={countContainer}>
          {countsSlot}
        </div>
        <div data-srp-component="metadata-actions" className={metadataActionControls}>
          <div id="srp-sort-control" data-srp-component="sort-control" className={actionControlItem}>
            {sortControl}
          </div>
          <div id="srp-save-search-control" data-srp-component="save-search-control" className={actionControlItem}>
            <SrpSaveSearchButton />
          </div>
        </div>
      </div>

      {isViewingDetectedArea && (
        <Banner tone="info" css={{ display: 'inline-flex' }}>
          {t('viewingDetectedAreaAlert')}
        </Banner>
      )}
    </div>
  );
}
